import { type ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  type ComboboxPresetBaseProps,
  type ComboboxRootProps,
} from './combobox';
import {
  buildVirtualizedHighlightHandler,
  type CreatableMarker,
  type CreatableProps,
  isCreatable,
  type QueryProps,
  useCreatableHelpers,
  VirtualizedList,
  type VirtualizerHandle,
} from './virtualized-internals';

export type PaginatedComboboxProps<T> = ComboboxPresetBaseProps<T> &
  Pick<
    ComboboxRootProps<T, false>,
    'value' | 'onValueChange' | 'isItemEqualToValue' | 'itemToStringLabel' | 'disabled'
  > &
  Omit<ComponentProps<typeof ComboboxInput>, 'value' | 'onValueChange' | 'children' | 'disabled'> &
  QueryProps &
  CreatableProps<T> & {
    /** Resolved item for the current `value` when the relevant page has not yet loaded. When provided, it labels the trigger before that page paginates in. Redundant when `value` is already in `items`. */
    selectedItem?: T;
    /** Whether a page is currently in-flight. Suppresses repeat `onLoadMore` fires while true. */
    isLoading?: boolean;
    /** Whether more pages remain. When false, the tail probe is not rendered. */
    hasMore?: boolean;
    /** Fired when the user scrolls near the bottom of the loaded set. Detected via an IntersectionObserver on a 1px probe element after the virtualized rows. */
    onLoadMore?: () => void;
  };

export const PaginatedCombobox = <T,>({
  items,
  value,
  selectedItem,
  onValueChange,
  getKey,
  getLabel,
  isItemEqualToValue,
  itemToStringLabel,
  emptyMessage,
  disabled,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  query,
  onQueryChange,
  onCreate,
  renderCreateOption,
  estimateSize,
  ...inputProps
}: PaginatedComboboxProps<T>) => {
  const { t } = useTranslation();
  const virtualizerRef = useRef<VirtualizerHandle | null>(null);
  const onItemHighlighted = useMemo(() => buildVirtualizedHighlightHandler(virtualizerRef), []);

  const isServerFilter = query !== undefined && onQueryChange !== undefined;
  const [internalQuery, setInternalQuery] = useState('');
  const effectiveQuery = isServerFilter ? query : internalQuery;
  const [inFlightCreatable, setInFlightCreatable] = useState<T | null>(null);

  const { itemsForView, localFilter, stableItemToStringLabel } = useCreatableHelpers(
    items,
    getLabel,
    itemToStringLabel,
    onCreate,
    effectiveQuery
  );

  // Default to key-based equality so cloned values (e.g. round-tripped through react-hook-form) still match items.
  const defaultIsItemEqualToValue = useCallback(
    (item: T, v: T) => {
      const itemIsCreatable = isCreatable(item);
      const valueIsCreatable = isCreatable(v);
      if (itemIsCreatable || valueIsCreatable) {
        return itemIsCreatable && valueIsCreatable && item.__create === v.__create;
      }
      return getKey(item) === getKey(v);
    },
    [getKey]
  );
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  const handleValueChange = useCallback<NonNullable<ComboboxRootProps<T, false>['onValueChange']>>(
    async (next, details) => {
      if (next && isCreatable(next) && onCreate) {
        setInFlightCreatable(next);

        try {
          const created = await onCreate((next as unknown as CreatableMarker).__create);
          onValueChange?.(created, details);
          setInFlightCreatable(null);
          if (isServerFilter) {
            onQueryChange('');
          } else {
            setInternalQuery('');
          }
        } catch {
          // Keep the creatable sticky so the user can edit the query and retry.
        }

        return;
      }

      setInFlightCreatable(null);
      onValueChange?.(next, details);
    },
    [onCreate, onValueChange, isServerFilter, onQueryChange]
  );

  return (
    <Combobox
      virtualized
      items={itemsForView}
      value={inFlightCreatable ?? selectedItem ?? value ?? null}
      onValueChange={handleValueChange}
      disabled={disabled}
      itemToStringLabel={stableItemToStringLabel}
      isItemEqualToValue={effectiveIsItemEqualToValue}
      inputValue={isServerFilter ? query : undefined}
      onInputValueChange={(next) => {
        if (inFlightCreatable !== null) {
          setInFlightCreatable(null);
        }
        if (isServerFilter) {
          onQueryChange(next);
        } else {
          setInternalQuery(next);
        }
      }}
      filter={isServerFilter ? null : localFilter}
      onItemHighlighted={onItemHighlighted}
    >
      <ComboboxInput disabled={disabled} {...inputProps} />
      <ComboboxContent>
        <ComboboxEmpty>{isLoading ? t('combobox-loading') : (emptyMessage ?? t('combobox-empty'))}</ComboboxEmpty>
        <VirtualizedList
          items={itemsForView}
          getKey={getKey}
          getLabel={getLabel}
          estimateSize={estimateSize}
          isLoading={isLoading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          renderCreateOption={renderCreateOption}
          isServerFilter={isServerFilter}
          virtualizerRef={virtualizerRef}
        />
      </ComboboxContent>
    </Combobox>
  );
};
